import { Award, Check, Lock } from 'lucide-react';
import { useState, useEffect } from 'react';
import { getUserBadges } from '../../lib/gamification/xpEngine';
import { useAuthStore } from '../../store/authStore';

interface Badge {
  id: string;
  name: string;
  description: string;
  icon_url: string;
  category: string;
  xp_reward: number;
  earned_at?: string;
}

interface UserBadge {
  id: string;
  badge_id: string;
  earned_at: string;
  displayed: boolean;
  badge: Badge;
}

interface BadgeDisplayProps {
  limit?: number;
  showAll?: boolean;
}

export function BadgeDisplay({ limit = 6, showAll = false }: BadgeDisplayProps) {
  const { user } = useAuthStore();
  const [badges, setBadges] = useState<UserBadge[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadBadges();
    }
  }, [user]);

  const loadBadges = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getUserBadges(user.id);
      setBadges(data);
    } catch (error) {
      console.error('Error loading badges:', error);
    } finally {
      setLoading(false);
    }
  };

  const displayedBadges = showAll ? badges : badges.slice(0, limit);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (badges.length === 0) {
    return (
      <div className="text-center py-8 px-4 bg-gray-50 rounded-lg">
        <Lock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600 font-medium">No badges earned yet</p>
        <p className="text-sm text-gray-500 mt-1">Complete your profile and apply to jobs to earn badges!</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
          <Award className="w-5 h-5 text-yellow-500" />
          Badges ({badges.length})
        </h3>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {displayedBadges.map((userBadge) => (
          <BadgeCard key={userBadge.id} badge={userBadge.badge} earnedAt={userBadge.earned_at} />
        ))}
      </div>

      {!showAll && badges.length > limit && (
        <button className="w-full py-2 text-center text-purple-600 font-medium hover:bg-purple-50 rounded-lg transition-colors">
          View All {badges.length} Badges
        </button>
      )}
    </div>
  );
}

interface BadgeCardProps {
  badge: Badge;
  earnedAt?: string;
}

function BadgeCard({ badge, earnedAt }: BadgeCardProps) {
  const isEarned = !!earnedAt;
  const earnedDate = earnedAt ? new Date(earnedAt) : null;

  return (
    <div
      className={`relative group rounded-xl p-4 border-2 transition-all ${
        isEarned
          ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-amber-50 hover:shadow-lg hover:scale-105'
          : 'border-gray-200 bg-gray-50 opacity-60'
      }`}
    >
      {/* Badge Icon */}
      <div className="relative mb-2">
        {badge.icon_url ? (
          <img
            src={badge.icon_url}
            alt={badge.name}
            className="w-16 h-16 mx-auto"
          />
        ) : (
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Award className="w-8 h-8 text-white" />
          </div>
        )}

        {isEarned && (
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      {/* Badge Info */}
      <h4 className="text-sm font-bold text-gray-900 text-center mb-1">{badge.name}</h4>

      {isEarned && earnedDate && (
        <p className="text-xs text-gray-500 text-center">
          {earnedDate.toLocaleDateString()}
        </p>
      )}

      {/* Hover Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block z-10">
        <div className="bg-gray-900 text-white text-xs rounded-lg p-3 w-48 shadow-xl">
          <p className="font-bold mb-1">{badge.name}</p>
          <p className="text-gray-300 mb-2">{badge.description}</p>
          <p className="text-yellow-400 font-semibold">+{badge.xp_reward} XP</p>
          <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-4 border-transparent border-t-gray-900"></div>
        </div>
      </div>
    </div>
  );
}
